import { useEffect, useState, useRef, useCallback } from 'react';

const FEEDBACK_NUDGE_EVENT = 'personalab:trigger-feedback-nudge';
const MAX_DISPLAYS_PER_SESSION = 3;
const MAX_ELIGIBLE_SESSIONS = 2;
const AUTO_HIDE_MS = 8000; // 8 segundos de auto-ocultação conforme solicitado

/**
 * Dispara o evento global de Nudge de Feedback após ações de alto valor (salvar persona, contexto, insights).
 */
export const triggerFeedbackNudge = () => {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(FEEDBACK_NUDGE_EVENT));
    }
};

/**
 * Hook para gerenciar o estado e as regras de exibição do Nudge de Feedback na Sidebar.
 */
export function useFeedbackNudge(userId?: string) {
    const [isNudgeVisible, setIsNudgeVisible] = useState(false);
    const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const dismissNudge = useCallback(() => {
        if (hideTimerRef.current) {
            clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
        }
        setIsNudgeVisible(false);
    }, []);

    useEffect(() => {
        if (!userId) return;

        const handleTrigger = () => {
            try {
                const totalSessionsKey = `personalab_feedback_nudge_total_sessions_${userId}`;
                const sessionRegisteredKey = `personalab_feedback_nudge_session_registered_${userId}`;
                const sessionCountKey = `personalab_feedback_nudge_session_count_${userId}`;

                const totalSessions = parseInt(localStorage.getItem(totalSessionsKey) || '0', 10);
                const isSessionRegistered = sessionStorage.getItem(sessionRegisteredKey) === 'true';

                // Regra 1: O ciclo se repete em no máximo 2 logins/sessões distintos por usuário
                if (totalSessions >= MAX_ELIGIBLE_SESSIONS && !isSessionRegistered) {
                    return;
                }

                // Regra 2: No máximo 3 exibições por sessão
                const sessionCount = parseInt(sessionStorage.getItem(sessionCountKey) || '0', 10);
                if (sessionCount >= MAX_DISPLAYS_PER_SESSION) {
                    return;
                }

                // Registra a sessão atual no localStorage se for a primeira vez nesta sessão
                if (!isSessionRegistered) {
                    const newTotalSessions = totalSessions + 1;
                    localStorage.setItem(totalSessionsKey, String(newTotalSessions));
                    sessionStorage.setItem(sessionRegisteredKey, 'true');
                }

                // Incrementa a contagem da sessão atual
                sessionStorage.setItem(sessionCountKey, String(sessionCount + 1));

                // Exibe o nudge
                setIsNudgeVisible(true);

                // Auto-oculta em 5 segundos
                if (hideTimerRef.current) {
                    clearTimeout(hideTimerRef.current);
                }

                hideTimerRef.current = setTimeout(() => {
                    setIsNudgeVisible(false);
                }, AUTO_HIDE_MS);
            } catch (err) {
                console.error('Erro ao gerenciar nudge de feedback:', err);
            }
        };

        window.addEventListener(FEEDBACK_NUDGE_EVENT, handleTrigger);

        return () => {
            window.removeEventListener(FEEDBACK_NUDGE_EVENT, handleTrigger);
            if (hideTimerRef.current) {
                clearTimeout(hideTimerRef.current);
            }
        };
    }, [userId]);

    return {
        isNudgeVisible,
        dismissNudge,
    };
}
